import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { getAdminMarginAccountDetail } from '../data/mockAdapter';

// ─── Helpers ────────────────────────────────────────────────────────────────

function healthFactorColor(hf: number): string {
  if (hf === 0) return 'text-red-600';
  if (hf < 1.2) return 'text-red-600';
  if (hf < 1.5) return 'text-yellow-600';
  return 'text-green-600';
}

function healthBarColor(hf: number): string {
  if (hf === 0) return 'bg-red-500';
  if (hf < 1.5) return 'bg-yellow-500';
  return 'bg-green-500';
}

function fmtCurrency(value: number): string {
  return `$${value.toLocaleString()}`;
}

const STATUS_CLASS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  warn: 'bg-yellow-100 text-yellow-700',
  default: 'bg-red-100 text-red-700',
  paused: 'bg-orange-100 text-orange-700',
  frozen: 'bg-blue-100 text-blue-700',
  liquidated: 'bg-red-100 text-red-700',
};

// ─── Page Component ─────────────────────────────────────────────────────────

export function AdminMarginAccountDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const account = accountId ? getAdminMarginAccountDetail(accountId) : null;

  const [accountStatus, setAccountStatus] = useState(account?.status ?? 'active');

  if (!account) {
    return (
      <div className="space-y-6">
        <Link to="/margin-accounts" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" /> Back to Margin Accounts
        </Link>
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <p className="text-sm text-muted-foreground">Margin account not found.</p>
        </div>
      </div>
    );
  }

  const hf = account.healthFactor;
  const hfPct = Math.min(100, Math.max(0, (hf / 3) * 100));

  function handleToggleFreeze() {
    const isFrozen = accountStatus === 'frozen';
    setAccountStatus(isFrozen ? 'active' : 'frozen');
    if (isFrozen) {
      toast.success(`Account ${account!.id} unfrozen`);
    } else {
      toast.error(`Account ${account!.id} frozen`);
    }
  }

  function handleClawback() {
    toast.success(`Clawback initiated on ${account!.id} (mock)`);
  }

  function handleMigrate() {
    toast.success(`Migration started for ${account!.id} (mock)`);
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Back link + status */}
      <div className="flex items-center justify-between">
        <Link to="/margin-accounts" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" /> Back to Margin Accounts
        </Link>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_CLASS[accountStatus] ?? 'bg-gray-100 text-gray-600'}`}>
          {accountStatus}
        </span>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold">{account.id}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {account.userName} &middot;{' '}
          <Link to={`/cf/${encodeURIComponent(account.facilityName)}`} className="text-primary hover:underline">
            {account.facilityName}
          </Link>
          {account.hasDdf && (
            <span className="ml-2 rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-xs font-medium">DDF Enabled</span>
          )}
        </p>
      </div>

      {/* Key Ratios */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-border p-5">
          <div className="text-xs text-muted-foreground">Health Factor</div>
          <div className={`mt-1 text-2xl font-bold ${healthFactorColor(hf)}`}>{hf.toFixed(2)}</div>
          {account.isAtRisk && (
            <div className="flex items-center gap-1 mt-1 text-xs text-yellow-600">
              <Icon name="streamline:warning-triangle" className="h-3 w-3" /> At risk
            </div>
          )}
        </div>
        <div className="rounded-xl border border-border p-5">
          <div className="text-xs text-muted-foreground">LTV</div>
          <div className="mt-1 text-2xl font-bold">{account.ltv}%</div>
          <div className="text-xs text-muted-foreground mt-1">Max {account.maxLtvPct}%</div>
        </div>
        <div className="rounded-xl border border-border p-5">
          <div className="text-xs text-muted-foreground">Total Owed</div>
          <div className="mt-1 text-2xl font-bold">{fmtCurrency(account.totalOwed)}</div>
          <div className="flex items-center gap-1 mt-1 text-xs text-yellow-600">
            <Icon name="streamline:graph-bar-increase" className="h-3 w-3" /> +${account.dailyAccrual}/day
          </div>
        </div>
        <div className="rounded-xl border border-border p-5">
          <div className="text-xs text-muted-foreground">Liq. Threshold</div>
          <div className="mt-1 text-2xl font-bold">{account.liquidationThresholdPct}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left column */}
        <div className="space-y-6">
          {/* Health Factor Detail */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Health Factor</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">Current</span>
                <span className={`text-3xl font-bold ${healthFactorColor(hf)}`}>{hf.toFixed(2)}</span>
              </div>
              <div className="h-3 rounded-full bg-muted overflow-hidden">
                <div className={`h-full rounded-full ${healthBarColor(hf)}`} style={{ width: `${hfPct}%` }} />
              </div>
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>Liquidation</span>
                <span>Safe</span>
              </div>
              {account.isAtRisk && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                  <Icon name="streamline:warning-triangle" className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-yellow-800">
                    Health factor is below 1.5. This account is at risk of liquidation.
                    {account.isLiquidatable && ' Account is liquidatable.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Collateral Breakdown */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Collateral Breakdown</CardTitle>
              <CardDescription>Tokens deposited as collateral.</CardDescription>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {account.collateralBreakdown.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No collateral data.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Token</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Amount</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {account.collateralBreakdown.map((c) => (
                        <tr key={c.token} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-2.5 font-medium">{c.token}</td>
                          <td className="px-4 py-2.5 text-right">{c.amount.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right text-muted-foreground">{c.pctOfTotal}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-2 text-sm text-right">
                Total Value: <span className="font-semibold">{fmtCurrency(account.collateralValueUsd)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Debt Summary */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Debt Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Principal Borrowed</span>
                  <span className="font-medium">{fmtCurrency(account.borrowedUsd)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Interest Accrued</span>
                  <span className="flex items-center gap-1 font-medium text-yellow-600">
                    {fmtCurrency(account.interestAccrued)}
                    <Icon name="streamline:graph-bar-increase" className="h-3 w-3" />
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Interest Rate</span>
                  <span className="font-medium">{account.interestRatePct}% APR</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Daily Accrual</span>
                  <span className="font-medium text-yellow-600">~${account.dailyAccrual}/day</span>
                </div>
                <div className="flex justify-between border-t border-border pt-3">
                  <span className="font-semibold">Total Owed</span>
                  <span className="text-2xl font-bold">{fmtCurrency(account.totalOwed)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Account Info */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Account Info</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Account ID</span>
                <span className="font-mono text-xs">{account.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Wallet</span>
                <span className="font-mono text-xs">{account.wallet.slice(0, 10)}…</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">User</span>
                <span className="font-medium">{account.userName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Facility</span>
                <Link to={`/cf/${encodeURIComponent(account.facilityName)}`} className="text-primary hover:underline text-xs">{account.facilityName}</Link>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Settlement</span>
                <span className="font-medium">{account.settlementToken}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Payment</span>
                <span className="font-medium">{account.lastPayment}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Next Payment</span>
                <span className="font-medium">{account.nextPayment}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Available Credit</span>
                <span className="font-medium">{fmtCurrency(account.availableCredit)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Admin Actions */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Admin Actions</CardTitle>
              <CardDescription>
                Compliance actions on this margin account. DDF liquidation is auto-triggered
                by searchers — not an admin action.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-3">
              {accountStatus === 'liquidated' ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                  <p className="text-sm text-red-800 font-medium">Account has been liquidated</p>
                </div>
              ) : (
                <>
                  <Button
                    variant={accountStatus === 'frozen' ? 'default' : 'destructive'}
                    className="w-full"
                    onClick={handleToggleFreeze}
                  >
                    <Icon name="streamline:snow-flake" className="mr-2 h-4 w-4" />
                    {accountStatus === 'frozen' ? 'Unfreeze Account' : 'Freeze Account'}
                  </Button>
                  <Button variant="outline" className="w-full" onClick={handleClawback}>
                    <Icon name="streamline:interface-arrows-synchronize-arrows-loading-load-sync-synchronize-arrow-reload" className="mr-2 h-4 w-4" />
                    Clawback
                  </Button>
                  <Button variant="outline" className="w-full" onClick={handleMigrate}>
                    <Icon name="streamline:interface-arrows-data-transfer-vertical-arrow-square-data-internet-transfer-network-vertical" className="mr-2 h-4 w-4" />
                    Migrate / Recover
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
